import React, { useEffect, useMemo, useState } from 'react';
import { kpiAPI } from '../services/api';
import { HomeIcon, ChartBarIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from '../components/NotificationBell';

export default function ManagerKpi({ mode = null, showHeader = true }) {
  const navigate = useNavigate();
  const { logout, isSuperAdmin } = useAuth();
  
  // Determine if we're in admin mode
  const isAdminMode = mode === 'admin' || (mode === null && isSuperAdmin());
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [active, setActive] = useState(null); // active submission for rating panel
  const [saving, setSaving] = useState(false);

  useEffect(()=>{ (async()=>{ try{ const { data } = await kpiAPI.listCycles(); setCycles(data); if(data?.length){ setCycleId(data[0].id);} }catch(e){} })(); },[]);

  const load = async () => {
    if (!cycleId) return;
    setLoading(true); setError('');
    try { 
      // Use admin API for admin mode, team API for manager mode
      if (isAdminMode) {
        const { data } = await kpiAPI.listSubmissionsAdmin({ cycleId, status, q });
        setSubs(data || []);
      } else {
        const { data } = await kpiAPI.listTeamSubmissions({ cycleId, status, q });
        setSubs(data || []);
      }
    }
    catch(e){ 
      setError(e.response?.data?.error || e.message); 
    }
    finally{ setLoading(false); }
  };

  useEffect(()=>{ load(); },[cycleId, status]);

  const getAverage = (submission, role) => {
    let a;
    if (role === 'admin') {
      // Find admin assessment (workaround for database role issue)
      a = (submission?.assessments || []).find(x => x.role !== 'self' && x.role !== 'manager');
      // If no non-self assessment found, look for empty role (database issue workaround)
      if (!a) {
        a = (submission?.assessments || []).find(x => x.role === '');
      }
    } else {
      a = (submission?.assessments || []).find(x => x.role === role);
    }
    
    if (!a || !Array.isArray(a.ratings) || a.ratings.length === 0) return null;
    const sum = a.ratings.reduce((s,r)=> s + (Number(r.score)||0), 0);
    return Math.round((sum / a.ratings.length) * 10)/10;
  };

  const stats = useMemo(()=>{
    const total = subs.length;
    const byStatus = subs.reduce((acc, s)=>{ acc[s.status] = (acc[s.status]||0)+1; return acc; }, {});
    const selfScores = subs.map(s=>getAverage(s,'self')).filter(v=>v!=null);
    const mgrScores = subs.map(s=>getAverage(s,'manager')).filter(v=>v!=null);
    const adminScores = subs.map(s=>getAverage(s,'admin')).filter(v=>v!=null);
    const avgSelf = selfScores.length ? (selfScores.reduce((a,b)=>a+b,0)/selfScores.length).toFixed(1) : '-';
    const avgMgr = mgrScores.length ? (mgrScores.reduce((a,b)=>a+b,0)/mgrScores.length).toFixed(1) : '-';
    const avgAdmin = adminScores.length ? (adminScores.reduce((a,b)=>a+b,0)/adminScores.length).toFixed(1) : '-';
    return { total, byStatus, avgSelf, avgMgr, avgAdmin };
  }, [subs]);

  const openRate = (s) => {
    const overallComment = isAdminMode ? getAdminOverallComment(s) : getManagerOverallComment(s);
    const ratings = buildCurrentRatings(s);
    setActive({ ...s, ratings, overallComment });
  };

  const buildCurrentRatings = (s) => {
    // If assessment exists for current mode, start from it; otherwise default 3 for each item
    let assessment;
    
    if (isAdminMode) {
      // For admin mode, find assessment that's not 'self' (workaround for database role issue)
      assessment = (s.assessments || []).find(x => x.role !== 'self' && x.role !== 'manager');
      // If no non-self assessment found, look for empty role (database issue workaround)
      if (!assessment) {
        assessment = (s.assessments || []).find(x => x.role === '');
      }
    } else {
      assessment = (s.assessments || []).find(x => x.role === 'manager');
    }
    
    if (assessment && Array.isArray(assessment.ratings)) {
      // Map existing ratings and fill in missing ones with defaults
      const existingRatings = assessment.ratings.map(r=>({ itemId: r.itemId, score: r.score||3, comments: r.comments||'' }));
      const itemIds = new Set(existingRatings.map(r => r.itemId));
      
      // Add missing items with default values
      const missingItems = (s.items || []).filter(item => !itemIds.has(item.id));
      const missingRatings = missingItems.map(item => ({ itemId: item.id, score: 3, comments: '' }));
      
      return [...existingRatings, ...missingRatings];
    }
    
    return (s.items||[]).map(it=>({ itemId: it.id, score: 3, comments: '' }));
  };

  const getManagerOverallComment = (s) => {
    const mgr = (s.assessments || []).find(x=>x.role==='manager');
    return mgr?.overallComment || '';
  };

  const getAdminOverallComment = (s) => {
    // Find admin assessment (workaround for database role issue)
    let admin = (s.assessments || []).find(x => x.role !== 'self' && x.role !== 'manager');
    // If no non-self assessment found, look for empty role (database issue workaround)
    if (!admin) {
      admin = (s.assessments || []).find(x => x.role === '');
    }
    return admin?.overallComment || '';
  };

  const saveAssessment = async (finalize) => {
    if (!active) return;
    try {
      setSaving(true); setError('');
      
      // Use admin endpoint for admin mode, manager endpoint for manager mode
      if (isAdminMode) {
        await kpiAPI.submitAdminAssessment(active.id, { ratings: active.ratings, overallComment: active.overallComment||'', finalize });
      } else {
        await kpiAPI.submitManagerAssessment(active.id, { ratings: active.ratings, overallComment: active.overallComment||'', finalize });
      }
      await load();
      setActive(null);
    } catch(e){ 
      setError(e.response?.data?.error || e.message); 
    }
    finally{ setSaving(false); }
  };

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      {/* Manager header look - conditionally shown */}
      {showHeader && (
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border-2 border-white/40 shadow-2xl ring-4 ring-white/10">
                  <ChartBarIcon className="h-10 w-10 text-white" />
                </div>
              </div>
              <div className="ml-5">
                <h1 className="text-2xl font-bold text-white drop-shadow-lg">
                  {isAdminMode ? 'KPI Admin Assessment' : 'KPI Manager'}
                </h1>
                <p className="text-sm text-white/90 mt-1 font-medium">
                  {isAdminMode ? 'Review and assess all KPI submissions' : 'Review and rate your team\'s KPIs'}
                </p>
              </div>
            </div>
            {/* Actions - right side, matching ManagerDashboard */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={()=>navigate('/manager-dashboard')}
                className="inline-flex items-center px-3 sm:px-6 py-2 sm:py-3 border-2 border-white/30 text-xs sm:text-sm font-semibold rounded-xl text-white bg-white/10 backdrop-blur-md hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/30 transition-all duration-200 shadow-xl hover:scale-105"
                title="Dashboard"
              >
                <HomeIcon className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
              <NotificationBell />
              <button
                onClick={logout}
                className="inline-flex items-center px-3 sm:px-6 py-2 sm:py-3 border-2 border-white/30 text-xs sm:text-sm font-semibold rounded-xl text-white bg-white/10 backdrop-blur-md hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/30 transition-all duration-200 shadow-xl hover:scale-105"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Filters and stats */}
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white border rounded p-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sm text-gray-600">Cycle</label>
              <select className="border rounded px-2 py-1" value={cycleId} onChange={e=>setCycleId(e.target.value)}>
                {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <label className="text-sm text-gray-600 ml-3">Status</label>
              <select className="border rounded px-2 py-1" value={status} onChange={e=>setStatus(e.target.value)}>
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="finalized">Finalized</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search name/email" className="border rounded px-2 py-1 text-sm flex-1" />
              <button onClick={load} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded">Apply</button>
            </div>
          </div>
          <div className="bg-white border rounded p-4">
            <div className="text-xs text-gray-500">Team KPI Stats</div>
            <div className="mt-2 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-2xl font-semibold">{stats.total}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
              <div>
                <div className="text-2xl font-semibold">{stats.byStatus.submitted || 0}</div>
                <div className="text-xs text-gray-500">Submitted</div>
              </div>
              <div>
                <div className="text-2xl font-semibold">{stats.byStatus.finalized || 0}</div>
                <div className="text-xs text-gray-500">Finalized</div>
              </div>
            </div>
          </div>
          <div className="bg-white border rounded p-4">
            <div className="text-xs text-gray-500">Average Scores</div>
            <div className={`mt-2 grid ${isAdminMode ? 'grid-cols-3' : 'grid-cols-2'} gap-3 text-center`}>
              <div>
                <div className="text-2xl font-semibold">{stats.avgSelf}</div>
                <div className="text-xs text-gray-500">Self</div>
              </div>
              <div>
                <div className="text-2xl font-semibold">{stats.avgMgr}</div>
                <div className="text-xs text-gray-500">Manager</div>
              </div>
              {isAdminMode && (
                <div>
                  <div className="text-2xl font-semibold">{stats.avgAdmin}</div>
                  <div className="text-xs text-gray-500">Admin</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && <div className="mb-4 text-red-600 text-sm bg-red-50 border border-red-200 rounded p-3">{error}</div>}
        {loading ? (
          <div className="bg-white border rounded p-8 text-center">
            <div className="text-gray-500">Loading team submissions...</div>
          </div>
        ) : (
          <div className="bg-white border rounded overflow-x-auto shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Self Avg</th>
                  {isAdminMode && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager Avg</th>
                  )}
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {isAdminMode ? 'Admin Avg' : 'Mgr Avg'}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subs.length === 0 ? (
                  <tr>
                    <td colSpan={isAdminMode ? "7" : "6"} className="px-4 py-8 text-center text-gray-500">
                      No team submissions found for the selected cycle and filters.
                    </td>
                  </tr>
                ) : (
                  subs.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">{s.employee?.firstName} {s.employee?.lastName}</td>
                      <td className="px-4 py-3 text-gray-600">{s.items?.length || 0}</td>
                      <td className="px-4 py-3 text-gray-600">{getAverage(s, 'self') ?? '-'}</td>
                      {isAdminMode && (
                        <td className="px-4 py-3 text-gray-600">{getAverage(s, 'manager') ?? '-'}</td>
                      )}
                      <td className="px-4 py-3 text-gray-600">{getAverage(s, isAdminMode ? 'admin' : 'manager') ?? '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          s.status === 'finalized' ? 'bg-green-100 text-green-800' :
                          s.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button 
                          className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors" 
                          onClick={()=>openRate(s)}
                        >
                          Rate
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Rating panel */}
        {active && (
          <div className="fixed inset-0 bg-black/30 flex items-end sm:items-center justify-center p-3 z-50">
            <div className="bg-white w-full sm:max-w-2xl rounded shadow-lg overflow-hidden">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <div className="font-medium">Rate: {active.employee?.firstName} {active.employee?.lastName}</div>
                <button className="text-gray-500" onClick={()=>setActive(null)}>Close</button>
              </div>
              <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
                {(active.items||[]).map(it => {
                  const selfRating = (active.assessments || []).find(a => a.role === 'self')?.ratings?.find(r => r.itemId === it.id);
                  const managerRating = (active.assessments || []).find(a => a.role === 'manager')?.ratings?.find(r => r.itemId === it.id);
                  return (
                    <div key={it.id} className="p-3 border rounded">
                      <div className="font-medium text-gray-800">{it.title}</div>
                      <div className="text-xs text-gray-600 mb-2">{it.description || ''}</div>
                      <div className={`grid gap-3 ${isAdminMode ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Employee Self-Rating</label>
                          <div className="text-sm font-medium text-blue-600">
                            {selfRating ? `${selfRating.score}/5` : 'Not rated'}
                          </div>
                        </div>
                        {!isAdminMode && (
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Your Manager Rating (1–5)</label>
                            <input type="number" min={1} max={5} className="border rounded px-2 py-1 w-20 text-center"
                              value={active.ratings.find(r=>r.itemId===it.id)?.score || 3}
                              onChange={e=>{
                                const v = Math.max(1, Math.min(5, Number(e.target.value||3)));
                                setActive(prev => ({ ...prev, ratings: prev.ratings.map(r=> r.itemId===it.id ? { ...r, score: v } : r) }));
                              }} />
                          </div>
                        )}
                        {isAdminMode && (
                          <>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Manager Rating</label>
                              <div className="text-sm font-medium text-green-600">
                                {managerRating ? `${managerRating.score}/5` : 'Not rated'}
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Your Admin Rating (1–5)</label>
                              <input type="number" min={1} max={5} className="border rounded px-2 py-1 w-20 text-center"
                                value={active.ratings.find(r=>r.itemId===it.id)?.score || 3}
                                onChange={e=>{
                                  const v = Math.max(1, Math.min(5, Number(e.target.value||3)));
                                  setActive(prev => ({ ...prev, ratings: prev.ratings.map(r=> r.itemId===it.id ? { ...r, score: v } : r) }));
                                }} />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Manager Comment (read-only in admin mode) */}
                {isAdminMode && getManagerOverallComment(active) && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Manager Comment</label>
                    <div className="border rounded w-full px-3 py-2 bg-gray-50 text-gray-700" rows={3}>
                      {getManagerOverallComment(active)}
                    </div>
                  </div>
                )}
                
                {/* Overall Comment (editable) */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {isAdminMode ? 'Your Admin Comment' : 'Overall Comment'}
                  </label>
                  <textarea className="border rounded w-full px-3 py-2" rows={3} value={active.overallComment}
                    onChange={e=>setActive(prev=>({ ...prev, overallComment: e.target.value }))} />
                </div>
              </div>
              <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
                <button disabled={saving} className="px-3 py-2 rounded border" onClick={()=>saveAssessment(false)}>
                  {isAdminMode ? 'Save Admin Assessment' : 'Save'}
                </button>
                <button disabled={saving} className="px-3 py-2 rounded bg-green-600 text-white" onClick={()=>saveAssessment(true)}>
                  {isAdminMode ? 'Finalize Admin Assessment' : 'Finalize'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}


